import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { payrollAPI } from '../../../../services/api';

const OFFICIAL_RATES = {
  cnss_employee_court_terme: 0.52,
  cnss_employee_long_terme: 3.96,
  cnss_employee_amo: 2.26,
  cnss_employer_allocations_familiales: 6.40,
  cnss_employer_court_terme: 1.05,
  cnss_employer_long_terme: 7.93,
  cnss_employer_amo: 4.11,
  cnss_employer_formation_pro: 1.60,
  cnss_ceiling_mad: 6000,
  frais_pro_rate: 20,
  frais_pro_monthly_cap: 2500,
};

const RateField = ({ label, value, onChange, disabled, suffix = '%' }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="flex items-center gap-1">
      <Input
        type="number"
        step="0.01"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        disabled={disabled}
        className="h-8 text-sm"
      />
      <span className="text-xs text-muted-foreground w-4">{suffix}</span>
    </div>
  </div>
);

const MoroccoPayrollForm = () => {
  const { t } = useTranslation();
  const [rates, setRates] = useState(OFFICIAL_RATES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await payrollAPI.getPayrollSettings();
        if (data?.morocco) setRates((prev) => ({ ...prev, ...data.morocco }));
      } catch {
        // fallback to official rates
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateRate = (key, val) => setRates((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    try {
      setSaving(true);
      await payrollAPI.updatePayrollSettings({ morocco: rates });
      toast.success(t('payrollMA.saveSuccess'));
    } catch {
      toast.error(t('payrollCfg.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = () => {
    setRates(OFFICIAL_RATES);
    toast.info(t('payrollCfg.restored'));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">🇲🇦 {t('payrollMA.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('payrollMA.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRestore}>
            <RotateCcw className="h-4 w-4 mr-1" /> {t('payrollCfg.restore')}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-1" /> {saving ? t('payrollCfg.saving') : t('payrollCfg.save')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payrollMA.empCnssCard')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <RateField label={t('payrollMA.shortTermEmp')} value={rates.cnss_employee_court_terme} onChange={(v) => updateRate('cnss_employee_court_terme', v)} />
            <RateField label={t('payrollMA.longTermEmp')} value={rates.cnss_employee_long_terme} onChange={(v) => updateRate('cnss_employee_long_terme', v)} />
            <RateField label={t('payrollMA.amoEmp')} value={rates.cnss_employee_amo} onChange={(v) => updateRate('cnss_employee_amo', v)} />
            <div className="pt-2 border-t">
              <RateField label={t('payrollMA.ceilingLabel')} value={rates.cnss_ceiling_mad} onChange={(v) => updateRate('cnss_ceiling_mad', v)} suffix="MAD" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payrollMA.employerCnssCard')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <RateField label={t('payrollMA.famAlloc')} value={rates.cnss_employer_allocations_familiales} onChange={(v) => updateRate('cnss_employer_allocations_familiales', v)} />
            <RateField label={t('payrollMA.shortTermEmployer')} value={rates.cnss_employer_court_terme} onChange={(v) => updateRate('cnss_employer_court_terme', v)} />
            <RateField label={t('payrollMA.longTermEmployer')} value={rates.cnss_employer_long_terme} onChange={(v) => updateRate('cnss_employer_long_terme', v)} />
            <RateField label={t('payrollMA.amoEmployer')} value={rates.cnss_employer_amo} onChange={(v) => updateRate('cnss_employer_amo', v)} />
            <RateField label={t('payrollMA.formationPro')} value={rates.cnss_employer_formation_pro} onChange={(v) => updateRate('cnss_employer_formation_pro', v)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payrollMA.irCard')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <RateField label={t('payrollMA.fraisProRate')} value={rates.frais_pro_rate} onChange={(v) => updateRate('frais_pro_rate', v)} />
            <RateField label={t('payrollMA.fraisProCap')} value={rates.frais_pro_monthly_cap} onChange={(v) => updateRate('frais_pro_monthly_cap', v)} suffix="MAD" />
            <p className="text-xs text-muted-foreground pt-1">{t('payrollMA.irNote')}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MoroccoPayrollForm;
